<template>
  <div class="main-panel">
    <div class="nav-bar-operations">
      <img src="@/assets/logogruas.png" alt="HomePage" class="logo-button" @click="goBack" />
    </div>

    <header class="header">
      <div class="header__left">
        <div class="avatar" aria-hidden="true">🔐</div>
        <div class="greet">
          <h1>Administrador de accesos</h1>
          <p class="sub">Gestiona grupos, permisos por grupo y visibilidad de permisos por usuario</p>
        </div>
      </div>
      <div class="header__right">
        <button class="btn btn--ghost" @click="refresh" :disabled="loading || saving">Actualizar</button>
      </div>
    </header>

    <section class="kpis">
      <article class="kpi">
        <span class="kpi__label">Grupos</span>
        <strong class="kpi__value">{{ groups.length }}</strong>
      </article>
      <article class="kpi">
        <span class="kpi__label">Permisos</span>
        <strong class="kpi__value">{{ permissions.length }}</strong>
      </article>
      <article class="kpi">
        <span class="kpi__label">Usuarios</span>
        <strong class="kpi__value">{{ users.length }}</strong>
      </article>
    </section>

    <div v-if="error" class="error-banner" role="alert">
      <span>{{ error }}</span>
      <button class="btn btn--ghost" @click="refresh">Reintentar</button>
    </div>
    <div v-if="success" class="success-banner" role="status">{{ success }}</div>

    <div class="tabs">
      <button
        class="tab"
        :class="{ 'tab--active': activeTab === 'groups' }"
        @click="activeTab = 'groups'"
      >
        Grupos y permisos
      </button>
      <button
        class="tab"
        :class="{ 'tab--active': activeTab === 'users' }"
        @click="activeTab = 'users'"
      >
        Usuarios y permisos efectivos
      </button>
    </div>

    <section v-if="activeTab === 'groups'" class="groups-layout">
      <article class="card groups-list-card">
        <div class="card__content">
          <h2>Grupos existentes</h2>
          <input
            v-model="groupFilter"
            type="text"
            class="input"
            placeholder="Filtrar grupos..."
          />

          <div class="group-list">
            <button
              v-for="group in filteredGroups"
              :key="group.id"
              class="group-row"
              :class="{ 'group-row--active': String(group.id) === String(selectedGroupId) }"
              @click="selectedGroupId = String(group.id)"
            >
              <div class="group-row__head">
                <span class="group-name">{{ group.name }}</span>
                <span class="badge">{{ group.permissions.length }} permisos</span>
              </div>
              <p class="group-preview">
                {{ group.permissions.slice(0, 2).map((p) => p.codename).join(', ') || 'Sin permisos' }}
              </p>
            </button>
            <p v-if="!filteredGroups.length" class="empty-copy">No hay grupos que coincidan.</p>
          </div>
        </div>
      </article>

      <article class="card">
        <div class="card__content">
          <h2>Crear grupo</h2>
          <div class="form-row">
            <input
              v-model="newGroupName"
              type="text"
              class="input"
              placeholder="Nombre del grupo"
            />
            <button class="btn btn--primary" @click="createGroup" :disabled="!canCreateGroup">
              Crear
            </button>
          </div>
          <p class="hint">La creación de permisos no se gestiona desde esta pantalla.</p>
        </div>
      </article>

      <article class="card card--editor">
        <div class="card__content">
          <div class="editor-head">
            <h2>Editar permisos de grupo</h2>
            <div class="editor-actions" v-if="selectedGroup">
              <button class="btn btn--danger" @click="deleteSelectedGroup" :disabled="saving">
                Eliminar grupo
              </button>
              <button class="btn btn--primary" @click="saveGroupPermissions" :disabled="!selectedGroup || saving">
                Guardar permisos
              </button>
            </div>
          </div>

          <template v-if="selectedGroup">
            <p class="selected-copy">
              Grupo seleccionado:
              <strong>{{ selectedGroup.name }}</strong>
            </p>
            <div class="permission-toolbar">
              <input
                v-model="permissionFilter"
                type="text"
                class="input"
                placeholder="Filtrar permisos por nombre o codename..."
              />
              <span class="count">{{ filteredPermissions.length }} permisos visibles</span>
            </div>

            <div class="permission-list">
              <div v-for="appBucket in groupedPermissions" :key="appBucket.key" class="permission-group">
                <h3 class="group-title">{{ appBucket.label }}</h3>
                <div class="group-items">
                  <label v-for="perm in appBucket.items" :key="perm.id" class="permission-item">
                    <input type="checkbox" :value="perm.id" v-model="editablePermissionIds" />
                    <div class="perm-text">
                      <span class="perm-name">{{ perm.name }}</span>
                      <span class="perm-code">{{ perm.app_label }}.{{ perm.codename }}</span>
                    </div>
                  </label>
                </div>
              </div>
            </div>
          </template>
          <p v-else class="empty-copy">Selecciona un grupo para editar sus permisos.</p>
        </div>
      </article>
    </section>

    <section v-else class="users-layout">
      <article class="card">
        <div class="card__content">
          <h2>Usuarios</h2>
          <input
            v-model="userFilter"
            type="text"
            class="input"
            placeholder="Filtrar por usuario o nombre..."
          />
          <p class="hint">
            La etiqueta
            <span class="pill pill--direct">Permiso directo</span>
            marca permisos asignados al usuario fuera de sus grupos.
          </p>

          <div class="users-grid">
            <article
              v-for="user in filteredUsers"
              :key="user.id"
              class="user-card"
              :class="{ 'user-card--inactive': user.is_active === false }"
            >
              <div v-if="user.is_active === false" class="inactive-watermark">INACTIVO</div>
              <div class="user-head">
                <div>
                  <h3>{{ user.full_name || user.username }}</h3>
                  <p>@{{ user.username }}</p>
                </div>
                <span class="badge">{{ user.effective_permissions.length }} permisos</span>
              </div>

              <div class="user-section">
                <h4>Grupos</h4>
                <div class="chip-list">
                  <span v-for="group in user.groups" :key="group.id" class="chip">
                    {{ group.name }}
                  </span>
                  <span v-if="!user.groups.length" class="chip chip--muted">Sin grupos</span>
                </div>
              </div>

              <div class="user-section">
                <h4>Permisos efectivos</h4>
                <div class="permissions-mini">
                  <span v-for="perm in user.effective_permissions" :key="perm.id" class="mini-perm">
                    {{ perm.codename }}
                    <span
                      v-if="isDirectOnly(user, perm)"
                      class="pill pill--direct"
                    >
                      directo
                    </span>
                  </span>
                  <span v-if="!user.effective_permissions.length" class="chip chip--muted">Sin permisos</span>
                </div>
              </div>
            </article>
            <p v-if="!filteredUsers.length" class="empty-copy">No hay usuarios que coincidan.</p>
          </div>
        </div>
      </article>
    </section>
  </div>
</template>

<script>
import axios from 'axios'

export default {
  name: 'AccessControlManager',
  data() {
    return {
      loading: false,
      saving: false,
      error: '',
      success: '',
      activeTab: 'groups',
      groups: [],
      permissions: [],
      users: [],
      selectedGroupId: '',
      editablePermissionIds: [],
      newGroupName: '',
      groupFilter: '',
      permissionFilter: '',
      userFilter: '',
    }
  },
  computed: {
    apiDirectory() {
      return process.env.API_DIRECTORY
    },
    canCreateGroup() {
      return this.newGroupName.trim().length > 0 && !this.saving
    },
    selectedGroup() {
      return this.groups.find((group) => String(group.id) === String(this.selectedGroupId)) || null
    },
    filteredGroups() {
      const needle = this.groupFilter.trim().toLowerCase()
      if (!needle) return this.groups
      return this.groups.filter((group) => group.name.toLowerCase().includes(needle))
    },
    filteredPermissions() {
      const needle = this.permissionFilter.trim().toLowerCase()
      if (!needle) return this.permissions
      return this.permissions.filter((perm) => {
        return (
          perm.name.toLowerCase().includes(needle) ||
          perm.codename.toLowerCase().includes(needle) ||
          perm.app_label.toLowerCase().includes(needle)
        )
      })
    },
    groupedPermissions() {
      const labelMap = {
        operations: 'Operaciones',
        accounting: 'Contabilidad',
        maintenance: 'Mantenimiento',
        rrhh: 'RRHH',
        dashboard: 'Dashboard',
        access_control: 'Acceso',
        auth: 'Auth',
      }
      const priority = [
        'access_control',
        'operations',
        'accounting',
        'maintenance',
        'rrhh',
        'dashboard',
        'auth',
      ]

      const buckets = new Map()
      this.filteredPermissions.forEach((perm) => {
        const key = perm.app_label || 'other'
        if (!buckets.has(key)) buckets.set(key, [])
        buckets.get(key).push(perm)
      })

      const orderedKeys = [
        ...priority.filter((key) => buckets.has(key)),
        ...[...buckets.keys()].filter((key) => !priority.includes(key)),
      ]

      return orderedKeys.map((key) => ({
        key,
        label: labelMap[key] || key,
        items: buckets.get(key),
      }))
    },
    filteredUsers() {
      const needle = this.userFilter.trim().toLowerCase()
      if (!needle) return this.users
      return this.users.filter((user) => {
        return (
          user.username.toLowerCase().includes(needle) ||
          (user.full_name || '').toLowerCase().includes(needle)
        )
      })
    },
  },
  watch: {
    selectedGroup() {
      this.syncEditablePermissions()
    },
  },
  created() {
    this.refresh()
  },
  methods: {
    goBack() {
      this.$router.push({ path: '/logs' })
    },
    clearMessages() {
      this.error = ''
      this.success = ''
    },
    async refresh() {
      this.loading = true
      this.clearMessages()
      try {
        await Promise.all([this.fetchPermissions(), this.fetchGroups(), this.fetchUsers()])
        this.ensureSelectedGroup()
      } catch (err) {
        this.error = 'No se pudieron cargar los datos del administrador de accesos.'
      } finally {
        this.loading = false
      }
    },
    ensureSelectedGroup() {
      if (!this.groups.length) {
        this.selectedGroupId = ''
        this.editablePermissionIds = []
        return
      }
      const exists = this.groups.some((group) => String(group.id) === String(this.selectedGroupId))
      if (!exists) this.selectedGroupId = String(this.groups[0].id)
      this.syncEditablePermissions()
    },
    syncEditablePermissions() {
      if (!this.selectedGroup) {
        this.editablePermissionIds = []
        return
      }
      this.editablePermissionIds = this.selectedGroup.permissions.map((perm) => perm.id)
    },
    async fetchPermissions() {
      const { data } = await axios.get(`${this.apiDirectory}/access-control/permissions`)
      this.permissions = Array.isArray(data) ? data : []
    },
    async fetchGroups() {
      const { data } = await axios.get(`${this.apiDirectory}/access-control/groups`)
      this.groups = Array.isArray(data) ? data : []
    },
    async fetchUsers() {
      const { data } = await axios.get(`${this.apiDirectory}/access-control/users`)
      this.users = Array.isArray(data) ? data : []
    },
    async createGroup() {
      if (!this.canCreateGroup) return
      this.saving = true
      this.clearMessages()
      try {
        const payload = { name: this.newGroupName.trim() }
        const { data } = await axios.post(`${this.apiDirectory}/access-control/groups/create`, payload)
        this.newGroupName = ''
        await this.fetchGroups()
        this.selectedGroupId = String(data.id)
        this.success = 'Grupo creado correctamente.'
      } catch (err) {
        this.error = 'No se pudo crear el grupo.'
      } finally {
        this.saving = false
      }
    },
    async deleteSelectedGroup() {
      if (!this.selectedGroup) return
      const confirmed = window.confirm(
        `Se eliminará el grupo "${this.selectedGroup.name}". Esta acción no se puede deshacer.`
      )
      if (!confirmed) return

      this.saving = true
      this.clearMessages()
      try {
        await axios.delete(`${this.apiDirectory}/access-control/groups/${this.selectedGroup.id}`)
        await Promise.all([this.fetchGroups(), this.fetchUsers()])
        this.ensureSelectedGroup()
        this.success = 'Grupo eliminado correctamente.'
      } catch (err) {
        this.error = 'No se pudo eliminar el grupo.'
      } finally {
        this.saving = false
      }
    },
    async saveGroupPermissions() {
      if (!this.selectedGroup) return
      this.saving = true
      this.clearMessages()
      try {
        await axios.post(`${this.apiDirectory}/access-control/groups/${this.selectedGroup.id}/permissions`, {
          permission_ids: this.editablePermissionIds,
        })
        await Promise.all([this.fetchGroups(), this.fetchUsers()])
        this.success = 'Permisos del grupo actualizados.'
      } catch (err) {
        this.error = 'No se pudieron guardar los permisos del grupo.'
      } finally {
        this.saving = false
      }
    },
    isDirectOnly(user, permission) {
      const directOnly = user.direct_only_permissions || []
      return directOnly.some((item) => item.id === permission.id)
    },
  },
}
</script>

<style scoped>
.main-panel {
  --bg: #f6f8fc;
  --surface: #ffffff;
  --surface-2: #f9fafb;
  --text: #0f172a;
  --muted: #64748b;
  --border: #dbe3ee;
  --brand: #ffc434;
  --brand-ink: #3a2a00;
  --primary: #1d4ed8;
  --danger: #dc2626;
  --success: #15803d;
  --ring: rgba(37, 99, 235, 0.22);
}

.main-panel {
  min-height: 100vh;
  background: var(--bg);
  padding: 12vh 24px 48px;
}

.nav-bar-operations {
  position: absolute;
  top: 0;
  left: 0;
  width: 100vw;
  height: 10vh;
  background: var(--brand);
  z-index: 1000;
  display: flex;
  align-items: center;
  padding-left: 16px;
  box-shadow: 0 2px 0 rgba(0, 0, 0, 0.05);
}

.logo-button {
  height: 55%;
  object-fit: contain;
  cursor: pointer;
}

.header {
  margin-top: 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.header__left {
  display: flex;
  align-items: center;
  gap: 14px;
}

.avatar {
  width: 44px;
  height: 44px;
  border-radius: 10px;
  display: grid;
  place-items: center;
  background: linear-gradient(135deg, var(--brand), #ffe188);
  color: var(--brand-ink);
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.12);
}

.greet h1 {
  margin: 0;
  font-size: 1.15rem;
  color: var(--text);
}

.sub {
  margin: 2px 0 0;
  color: var(--muted);
  font-size: 0.9rem;
}

.kpis {
  margin-top: 14px;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 10px;
}

.kpi {
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 12px;
  padding: 10px 12px;
}

.kpi__label {
  display: block;
  color: var(--muted);
  font-size: 0.8rem;
}

.kpi__value {
  font-size: 1.2rem;
  color: var(--text);
}

.error-banner,
.success-banner {
  margin-top: 12px;
  border-radius: 10px;
  padding: 10px 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.error-banner {
  border: 1px solid rgba(220, 38, 38, 0.25);
  background: rgba(220, 38, 38, 0.08);
  color: var(--danger);
}

.success-banner {
  border: 1px solid rgba(21, 128, 61, 0.25);
  background: rgba(21, 128, 61, 0.08);
  color: var(--success);
}

.tabs {
  margin-top: 14px;
  display: inline-flex;
  padding: 6px;
  border-radius: 14px;
  border: 1px solid #c9d6e6;
  background: #eaf0f8;
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.55);
  gap: 6px;
}

.tab {
  border: 1px solid transparent;
  padding: 9px 14px;
  border-radius: 10px;
  color: #475569;
  background: rgba(255, 255, 255, 0.55);
  cursor: pointer;
  font-weight: 600;
  font-size: 0.83rem;
  transition: background 0.2s ease, color 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease;
}

.tab:hover {
  background: rgba(255, 255, 255, 0.9);
  border-color: #bfccde;
  color: #334155;
}

.tab--active {
  color: #1e3a8a;
  background: #ffffff;
  border-color: #9fb6db;
  box-shadow: 0 3px 9px rgba(37, 99, 235, 0.16);
}

.groups-layout {
  margin-top: 14px;
  display: grid;
  gap: 14px;
  grid-template-columns: minmax(240px, 320px) minmax(280px, 380px) 1fr;
}

.users-layout {
  margin-top: 14px;
}

.card {
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 16px;
  box-shadow: 0 3px 10px rgba(15, 23, 42, 0.04);
}

.card__content {
  padding: 16px;
  display: grid;
  gap: 12px;
}

.card__content h2 {
  margin: 0;
  font-size: 1rem;
  color: var(--text);
}

.card--editor {
  min-height: 600px;
}

.input {
  width: 100%;
  padding: 10px 12px;
  border-radius: 10px;
  border: 1px solid var(--border);
  background: #fff;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 10px;
}

.group-list {
  max-height: 620px;
  overflow: auto;
  display: grid;
  gap: 8px;
}

.group-row {
  text-align: left;
  border: 1px solid var(--border);
  border-radius: 10px;
  background: var(--surface-2);
  padding: 10px;
  cursor: pointer;
}

.group-row--active {
  border-color: var(--primary);
  background: #eff6ff;
}

.group-row__head {
  display: flex;
  justify-content: space-between;
  gap: 8px;
  align-items: center;
}

.group-name {
  font-weight: 700;
  color: var(--text);
}

.group-preview {
  margin: 6px 0 0;
  font-size: 0.8rem;
  color: var(--muted);
}

.badge {
  font-size: 0.72rem;
  padding: 3px 8px;
  border-radius: 999px;
  background: #e2e8f0;
  color: #334155;
  white-space: nowrap;
}

.editor-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.editor-actions {
  display: flex;
  gap: 8px;
}

.selected-copy {
  margin: 0;
  color: var(--muted);
}

.permission-toolbar {
  display: flex;
  gap: 12px;
  align-items: center;
}

.count {
  color: var(--muted);
  font-size: 0.85rem;
  white-space: nowrap;
}

.permission-list {
  display: grid;
  gap: 14px;
  max-height: 560px;
  overflow: auto;
  padding-right: 4px;
}

.permission-group {
  display: grid;
  gap: 8px;
}

.group-title {
  margin: 0;
  font-size: 0.92rem;
  color: var(--text);
}

.group-items {
  display: grid;
  gap: 8px;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
}

.permission-item {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 8px;
  border: 1px solid var(--border);
  border-radius: 10px;
  background: #fff;
  min-width: 0;
}

.perm-text {
  display: grid;
  min-width: 0;
}

.perm-name {
  color: var(--text);
  font-weight: 600;
  line-height: 1.25;
  overflow-wrap: anywhere;
  word-break: break-word;
  white-space: normal;
}

.perm-code {
  color: var(--muted);
  font-size: 0.8rem;
  line-height: 1.2;
  overflow-wrap: anywhere;
  word-break: break-word;
  white-space: normal;
}

.users-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 12px;
}

.user-card {
  position: relative;
  border: 1px solid var(--border);
  border-radius: 12px;
  padding: 12px;
  background: var(--surface-2);
  display: grid;
  gap: 10px;
  max-height: 440px;
  overflow-y: auto;
}

.user-card--inactive {
  border-color: rgba(220, 38, 38, 0.35);
  background: #fff7f7;
  padding-top: 54px;
}

.inactive-watermark {
  position: absolute;
  top: 10px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 3;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin: 0;
  width: auto;
  font-size: 1rem;
  font-weight: 800;
  letter-spacing: 0.06em;
  color: #b91c1c;
  background: rgba(254, 226, 226, 0.95);
  border: 1px solid rgba(220, 38, 38, 0.3);
  border-radius: 8px;
  padding: 4px 12px;
  line-height: 1.1;
  white-space: nowrap;
  pointer-events: none;
}

.user-head {
  display: flex;
  justify-content: space-between;
  gap: 10px;
  align-items: flex-start;
}

.user-head .badge {
  align-self: flex-start;
  flex-shrink: 0;
}

.user-head h3 {
  margin: 0;
  color: var(--text);
  font-size: 0.95rem;
}

.user-head p {
  margin: 2px 0 0;
  color: var(--muted);
  font-size: 0.8rem;
}

.user-section h4 {
  margin: 0 0 6px;
  font-size: 0.82rem;
  color: var(--muted);
}

.chip-list,
.permissions-mini {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.chip {
  font-size: 0.75rem;
  padding: 3px 8px;
  border-radius: 999px;
  background: #dbeafe;
  color: #1e3a8a;
}

.chip--muted {
  background: #e2e8f0;
  color: #475569;
}

.mini-perm {
  display: inline-flex;
  align-items: flex-start;
  gap: 4px;
  font-size: 0.72rem;
  border-radius: 999px;
  padding: 3px 8px;
  background: #eef2ff;
  color: #3730a3;
  max-width: 100%;
  white-space: normal;
  overflow-wrap: anywhere;
  word-break: break-word;
}

.pill {
  display: inline-flex;
  align-items: center;
  padding: 2px 6px;
  border-radius: 999px;
  font-size: 0.65rem;
  font-weight: 700;
  text-transform: uppercase;
}

.pill--direct {
  color: #7f1d1d;
  background: #fee2e2;
}

.hint {
  margin: 0;
  color: var(--muted);
  font-size: 0.85rem;
}

.empty-copy {
  margin: 0;
  color: var(--muted);
  font-size: 0.9rem;
}

.btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 9px 13px;
  border-radius: 10px;
  border: 1px solid transparent;
  background: #0f172a;
  color: #fff;
  cursor: pointer;
}

.btn:focus {
  outline: none;
  box-shadow: 0 0 0 6px var(--ring);
}

.btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.btn--primary {
  background: var(--primary);
}

.btn--ghost {
  border-color: var(--border);
  background: #fff;
  color: var(--text);
}

.btn--danger {
  background: var(--danger);
}

@media (max-width: 1200px) {
  .groups-layout {
    grid-template-columns: 1fr;
  }

  .card--editor {
    min-height: 0;
  }
}
</style>
